import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject, of } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap, tap, catchError, finalize, map } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { DocumentTypesService } from '../document-types.service';
import { CommonService } from '../../../../services/common.service';
import { NotificationService } from '../../../../services/notification.service';
import { environment } from '../../../../../environments/environment';
import { helper } from '../../../../../environments/helper';

import {
    DocumentTypeFunctieAssignment,
    AssignmentDialogData,
    FunctieData,
    DirectieData,
    CompartimentData,
    UserData,
    AvailableSelectionsData,
    AssignmentValidationError
} from '../document-types.interface';

@Component({
    selector: 'app-document-type-functie-management',
    templateUrl: './document-type-functie-management.component.html',
    styleUrls: ['./document-type-functie-management.component.scss']
})
export class DocumentTypeFunctieManagementComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    assignmentForm: FormGroup;
    availableData: AvailableSelectionsData = {
        functii: [],
        directii: [],
        compartimente: [],
        users: []
    };

    // Store compartimente and users per directie/compartiment for better management
    compartimenteByDirectie: { [directieId: number]: CompartimentData[] } = {};
    usersByDirectieCompartiment: { [key: string]: UserData[] } = {};

    // Typeahead controls for each assignment
    functiiControls: { [index: number]: FormControl } = {};
    directiiControls: { [index: number]: FormControl } = {};
    compartimenteControls: { [index: number]: FormControl } = {};
    usersControls: { [index: number]: FormControl } = {};

    // Filtered data for typeahead
    filteredFunctiiByIndex: { [index: number]: FunctieData[] } = {};
    filteredDirectiiByIndex: { [index: number]: DirectieData[] } = {};
    filteredCompartimenteByIndex: { [index: number]: CompartimentData[] } = {};
    filteredUsersByIndex: { [index: number]: UserData[] } = {};

    // Error properties following the project pattern
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    helper = helper;

    loading = false;
    saving = false;
    validationErrors: AssignmentValidationError[] = [];

    constructor(
        public dialogRef: MatDialogRef<DocumentTypeFunctieManagementComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AssignmentDialogData,
        private fb: FormBuilder,
        private documentTypesService: DocumentTypesService,
        private commonService: CommonService,
        private notificationService: NotificationService
    ) {
        this.assignmentForm = this.fb.group({
            assignments: this.fb.array([])
        });
    }

    ngOnInit(): void {
        this.loadInitialData();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get assignmentsArray(): FormArray {
        return this.assignmentForm.get('assignments') as FormArray;
    }

    private loadInitialData(): void {
        this.loading = true;

        // Load available functii, directii
        Promise.all([
            this.loadAvailableFunctii(),
            this.loadAvailableDirectii()
        ]).then(async () => {
            await this.initializeExistingAssignments();
        }).catch((error) => {
            console.error('Error loading initial data:', error);
            this.showErrorMessage('Eroare la încărcarea datelor inițiale');
        }).finally(() => {
            this.loading = false;
        });
    }

    private async loadAvailableFunctii(): Promise<void> {
        try {
            const response = await this.documentTypesService.getAvailableFunctii().toPromise();
            if (response?.success) {
                this.availableData.functii = response.data;
            }
        } catch (error) {
            console.error('Error loading functii:', error);
            throw error;
        }
    }

    private async loadAvailableDirectii(): Promise<void> {
        try {
            const response = await this.documentTypesService.getAvailableDirectii().toPromise();
            if (response?.success) {
                this.availableData.directii = response.data;
            }
        } catch (error) {
            console.error('Error loading directii:', error);
            throw error;
        }
    }

    private async initializeExistingAssignments(): Promise<void> {
        this.assignmentsArray.clear();

        if (this.data.existingAssignments?.length > 0) {
            // Process existing assignments sequentially to ensure proper loading
            for (const assignment of this.data.existingAssignments) {
                await this.addAssignmentFormGroupWithData(assignment);
            }
        } else {
            // Add one empty assignment to start with
            this.addAssignmentFormGroup();
        }
    }

    private async addAssignmentFormGroupWithData(assignment: DocumentTypeFunctieAssignment): Promise<void> {
        const assignmentGroup = this.fb.group({
            id: [assignment?.id || null],
            functie_id: [assignment?.functie_id || null, Validators.required],
            directie_id: [assignment?.directie_id || null, Validators.required],
            compartiment_id: [assignment?.compartiment_id || -1],
            user_id: [assignment?.user_id || -1],
            order_priority: [assignment?.order_priority || this.assignmentsArray.length + 1]
        });

        const currentIndex = this.assignmentsArray.length;

        // Set up typeahead controls
        this.setupTypeaheadControls(currentIndex);

        // Set up cascading dropdowns
        this.setupCascadingSelections(assignmentGroup, currentIndex);

        this.assignmentsArray.push(assignmentGroup);

        // Load cascading data sequentially for existing assignments
        if (assignment?.directie_id) {
            // Load compartimente first
            await this.loadCompartimenteForAssignment(currentIndex, assignment.directie_id);

            // Then load users (with or without compartiment)
            if (assignment.compartiment_id) {
                await this.loadUsersForAssignment(currentIndex, assignment.directie_id, assignment.compartiment_id);
            } else {
                await this.loadUsersForAssignment(currentIndex, assignment.directie_id);
            }
        }
    }

    private addAssignmentFormGroup(assignment?: DocumentTypeFunctieAssignment): void {
        const assignmentGroup = this.fb.group({
            id: [assignment?.id || null],
            functie_id: [assignment?.functie_id || null, Validators.required],
            directie_id: [assignment?.directie_id || null, Validators.required],
            compartiment_id: [assignment?.compartiment_id || -1],
            user_id: [assignment?.user_id || -1],
            order_priority: [assignment?.order_priority || this.assignmentsArray.length + 1]
        });

        const currentIndex = this.assignmentsArray.length;

        // Set up typeahead controls
        this.setupTypeaheadControls(currentIndex);

        // Set up cascading dropdowns
        this.setupCascadingSelections(assignmentGroup, currentIndex);

        this.assignmentsArray.push(assignmentGroup);

        // Load initial compartimente and users if directie is already selected
        if (assignment?.directie_id) {
            this.loadCompartimenteForAssignment(currentIndex, assignment.directie_id);
            if (assignment.compartiment_id) {
                this.loadUsersForAssignment(currentIndex, assignment.directie_id, assignment.compartiment_id);
            } else {
                this.loadUsersForAssignment(currentIndex, assignment.directie_id);
            }
        }
    }

    private setupTypeaheadControls(index: number): void {
        // Initialize controls for this assignment
        this.functiiControls[index] = new FormControl();
        this.directiiControls[index] = new FormControl();
        this.compartimenteControls[index] = new FormControl();
        this.usersControls[index] = new FormControl();

        // Initialize filtered arrays
        this.filteredFunctiiByIndex[index] = [...this.availableData.functii];
        this.filteredDirectiiByIndex[index] = [...this.availableData.directii];
        this.filteredCompartimenteByIndex[index] = [];
        this.filteredUsersByIndex[index] = [];

        // Setup typeahead for Functii
        this.functiiControls[index].valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredFunctiiByIndex[index] = [...this.availableData.functii];
                    return of(this.availableData.functii);
                }
                return this.filterFunctii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredFunctiiByIndex[index] = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        // Setup typeahead for Directii
        this.directiiControls[index].valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredDirectiiByIndex[index] = [...this.availableData.directii];
                    return of(this.availableData.directii);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectiiByIndex[index] = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        // Setup typeahead for Compartimente
        this.compartimenteControls[index].valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredCompartimenteByIndex[index] = this.getCompartimenteForCurrentAssignment(index);
                    return of(this.getCompartimenteForCurrentAssignment(index));
                }
                return this.filterCompartimente(inputValue, index);
            }),
            tap((data: any[]) => {
                this.filteredCompartimenteByIndex[index] = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        // Setup typeahead for Users
        this.usersControls[index].valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredUsersByIndex[index] = this.getUsersForCurrentAssignment(index);
                    return of(this.getUsersForCurrentAssignment(index));
                }
                return this.filterUsers(inputValue, index);
            }),
            tap((data: any[]) => {
                this.filteredUsersByIndex[index] = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    private setupCascadingSelections(formGroup: FormGroup, index: number): void {
        // Watch directie changes
        formGroup.get('directie_id')?.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(300),
                distinctUntilChanged()
            )
            .subscribe((directieId) => {
                if (directieId) {
                    // Clear dependent fields
                    formGroup.patchValue({
                        compartiment_id: -1,
                        user_id: -1
                    });

                    // Load compartimente
                    this.loadCompartimenteForAssignment(index, directieId);

                    // Load users for directie only
                    this.loadUsersForAssignment(index, directieId);
                } else {
                    // Clear all dependent data
                    this.clearAssignmentSelections(index);
                }
            });

        // Watch compartiment changes
        formGroup.get('compartiment_id')?.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(300),
                distinctUntilChanged()
            )
            .subscribe((compartimentId) => {
                const directieId = formGroup.get('directie_id')?.value;
                if (directieId) {
                    // Clear user selection
                    formGroup.patchValue({ user_id: -1 });

                    // Load filtered users
                    this.loadUsersForAssignment(index, directieId, compartimentId);
                }
            });
    }

    private async loadCompartimenteForAssignment(assignmentIndex: number, directieId: number): Promise<void> {
        try {
            // Check if we already have compartimente for this directie
            if (!this.compartimenteByDirectie[directieId]) {
                const response = await this.documentTypesService.getCompartimenteByDirectie(directieId).toPromise();
                if (response?.success) {
                    this.compartimenteByDirectie[directieId] = response.data;
                }
            }

            // Update filtered compartimente for this assignment
            this.filteredCompartimenteByIndex[assignmentIndex] = this.compartimenteByDirectie[directieId] || [];
        } catch (error) {
            console.error('Error loading compartimente:', error);
        }
    }

    private async loadUsersForAssignment(assignmentIndex: number, directieId: number, compartimentId?: number): Promise<void> {
        try {
            const actualCompartimentId = compartimentId === -1 ? undefined : compartimentId;
            const cacheKey = `${directieId}_${actualCompartimentId || 'null'}`;

            // Check if we already have users for this combination
            if (!this.usersByDirectieCompartiment[cacheKey]) {
                const response = await this.documentTypesService.getAvailableUsers(
                    this.data.documentTypeId,
                    directieId,
                    actualCompartimentId
                ).toPromise();

                if (response?.success) {
                    this.usersByDirectieCompartiment[cacheKey] = response.data;
                }
            }

            // Update filtered users for this assignment
            this.filteredUsersByIndex[assignmentIndex] = this.usersByDirectieCompartiment[cacheKey] || [];
        } catch (error) {
            console.error('Error loading users:', error);
        }
    }

    private clearAssignmentSelections(assignmentIndex: number): void {
        // Clear filtered data for this assignment
        this.filteredCompartimenteByIndex[assignmentIndex] = [];
        this.filteredUsersByIndex[assignmentIndex] = [];
    }

    // Typeahead filter methods
    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterFunctii(searchTerm: string) {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const filtered = this.availableData.functii.filter(functie =>
            this.normalizeString(functie.name).includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    filterDirectii(searchTerm: string) {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const filtered = this.availableData.directii.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    filterCompartimente(searchTerm: string, assignmentIndex: number) {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const availableCompartimente = this.getCompartimenteForCurrentAssignment(assignmentIndex);
        const filtered = availableCompartimente.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    filterUsers(searchTerm: string, assignmentIndex: number) {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const availableUsers = this.getUsersForCurrentAssignment(assignmentIndex);
        const filtered = availableUsers.filter(user =>
            this.normalizeString(user.name || user.full_name || '').includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    // Typeahead close handlers
    onFunctieSelectClosed(assignmentIndex: number) {
        const selectedValue = this.functiiControls[assignmentIndex].value;
        if (selectedValue) {
            const assignment = this.assignmentsArray.at(assignmentIndex);
            const isSelectedInFilteredList = this.filteredFunctiiByIndex[assignmentIndex].some(functie =>
                functie.id === assignment?.get('functie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.functiiControls[assignmentIndex].setValue(null);
                this.filteredFunctiiByIndex[assignmentIndex] = [...this.availableData.functii];
            }
        }
    }

    onDirectieSelectClosed(assignmentIndex: number) {
        const selectedValue = this.directiiControls[assignmentIndex].value;
        if (selectedValue) {
            const assignment = this.assignmentsArray.at(assignmentIndex);
            const isSelectedInFilteredList = this.filteredDirectiiByIndex[assignmentIndex].some(directie =>
                directie.id_directie === assignment?.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControls[assignmentIndex].setValue(null);
                this.filteredDirectiiByIndex[assignmentIndex] = [...this.availableData.directii];
            }
        }
    }

    onCompartimentSelectClosed(assignmentIndex: number) {
        const selectedValue = this.compartimenteControls[assignmentIndex].value;
        if (selectedValue) {
            const assignment = this.assignmentsArray.at(assignmentIndex);
            const isSelectedInFilteredList = this.filteredCompartimenteByIndex[assignmentIndex].some(compartiment =>
                compartiment.id_compartiment === assignment?.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControls[assignmentIndex].setValue(null);
                this.filteredCompartimenteByIndex[assignmentIndex] = this.getCompartimenteForCurrentAssignment(assignmentIndex);
            }
        }
    }

    onUserSelectClosed(assignmentIndex: number) {
        const selectedValue = this.usersControls[assignmentIndex].value;
        if (selectedValue) {
            const assignment = this.assignmentsArray.at(assignmentIndex);
            const isSelectedInFilteredList = this.filteredUsersByIndex[assignmentIndex].some(user =>
                user.id === assignment?.get('user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControls[assignmentIndex].setValue(null);
                this.filteredUsersByIndex[assignmentIndex] = this.getUsersForCurrentAssignment(assignmentIndex);
            }
        }
    }

    // Get compartimente for a specific directie
    getCompartimenteForDirectie(directieId: number): CompartimentData[] {
        return this.compartimenteByDirectie[directieId] || [];
    }

    // Get users for a specific directie/compartiment combination
    getUsersForDirectieCompartiment(directieId: number, compartimentId?: number): UserData[] {
        const cacheKey = `${directieId}_${compartimentId === -1 || !compartimentId ? 'null' : compartimentId}`;
        return this.usersByDirectieCompartiment[cacheKey] || [];
    }

    // Get compartimente for current assignment based on selected directie
    getCompartimenteForCurrentAssignment(assignmentIndex: number): CompartimentData[] {
        const assignment = this.assignmentsArray.at(assignmentIndex);
        const directieId = assignment?.get('directie_id')?.value;
        return directieId ? this.getCompartimenteForDirectie(directieId) : [];
    }

    // Get users for current assignment based on selected directie/compartiment
    getUsersForCurrentAssignment(assignmentIndex: number): UserData[] {
        const assignment = this.assignmentsArray.at(assignmentIndex);
        const directieId = assignment?.get('directie_id')?.value;
        const compartimentId = assignment?.get('compartiment_id')?.value;
        const actualCompartimentId = compartimentId === -1 ? undefined : compartimentId;
        return directieId ? this.getUsersForDirectieCompartiment(directieId, actualCompartimentId) : [];
    }

    // Load compartimente when compartiment dropdown is opened
    async onCompartimentDropdownOpened(opened: boolean, assignmentIndex: number): Promise<void> {
        if (opened) {
            const assignment = this.assignmentsArray.at(assignmentIndex);
            const directieId = assignment?.get('directie_id')?.value;
            if (directieId && !this.compartimenteByDirectie[directieId]) {
                await this.loadCompartimenteForAssignment(assignmentIndex, directieId);
            }
        }
    }

    // Load users when user dropdown is opened
    async onUserDropdownOpened(opened: boolean, assignmentIndex: number): Promise<void> {
        if (opened) {
            const assignment = this.assignmentsArray.at(assignmentIndex);
            const directieId = assignment?.get('directie_id')?.value;
            const compartimentId = assignment?.get('compartiment_id')?.value;
            if (directieId) {
                const actualCompartimentId = compartimentId === -1 ? undefined : compartimentId;
                const cacheKey = `${directieId}_${actualCompartimentId || 'null'}`;
                if (!this.usersByDirectieCompartiment[cacheKey]) {
                    await this.loadUsersForAssignment(assignmentIndex, directieId, actualCompartimentId);
                }
            }
        }
    }

    addNewAssignment(): void {
        this.addAssignmentFormGroup();
    }

    removeAssignment(index: number): void {
        if (this.assignmentsArray.length > 1) {
            // Clean up typeahead controls
            delete this.functiiControls[index];
            delete this.directiiControls[index];
            delete this.compartimenteControls[index];
            delete this.usersControls[index];

            delete this.filteredFunctiiByIndex[index];
            delete this.filteredDirectiiByIndex[index];
            delete this.filteredCompartimenteByIndex[index];
            delete this.filteredUsersByIndex[index];

            this.assignmentsArray.removeAt(index);
            this.reorderAssignments();

            // Reindex the remaining controls
            this.reindexTypeaheadControls(index);
        }
    }

    private reindexTypeaheadControls(removedIndex: number): void {
        // Shift all controls down by one index for items after the removed index
        const maxIndex = Math.max(...Object.keys(this.functiiControls).map(k => parseInt(k)));

        for (let i = removedIndex; i < maxIndex; i++) {
            const nextIndex = i + 1;
            if (this.functiiControls[nextIndex]) {
                this.functiiControls[i] = this.functiiControls[nextIndex];
                this.directiiControls[i] = this.directiiControls[nextIndex];
                this.compartimenteControls[i] = this.compartimenteControls[nextIndex];
                this.usersControls[i] = this.usersControls[nextIndex];

                this.filteredFunctiiByIndex[i] = this.filteredFunctiiByIndex[nextIndex];
                this.filteredDirectiiByIndex[i] = this.filteredDirectiiByIndex[nextIndex];
                this.filteredCompartimenteByIndex[i] = this.filteredCompartimenteByIndex[nextIndex];
                this.filteredUsersByIndex[i] = this.filteredUsersByIndex[nextIndex];

                delete this.functiiControls[nextIndex];
                delete this.directiiControls[nextIndex];
                delete this.compartimenteControls[nextIndex];
                delete this.usersControls[nextIndex];

                delete this.filteredFunctiiByIndex[nextIndex];
                delete this.filteredDirectiiByIndex[nextIndex];
                delete this.filteredCompartimenteByIndex[nextIndex];
                delete this.filteredUsersByIndex[nextIndex];
            }
        }
    }

    onDrop(event: CdkDragDrop<any>): void {
        if (event.previousIndex !== event.currentIndex) {
            const assignmentsArray = this.assignmentsArray;

            // Move the FormControl
            const item = assignmentsArray.at(event.previousIndex);
            assignmentsArray.removeAt(event.previousIndex);
            assignmentsArray.insert(event.currentIndex, item);

            // Also move the typeahead controls
            this.moveTypeaheadControls(event.previousIndex, event.currentIndex);

            this.reorderAssignments();
        }
    }

    private moveTypeaheadControls(fromIndex: number, toIndex: number): void {
        // Store the controls and filtered data
        const functieControl = this.functiiControls[fromIndex];
        const directieControl = this.directiiControls[fromIndex];
        const compartimentControl = this.compartimenteControls[fromIndex];
        const userControl = this.usersControls[fromIndex];

        const filteredFunctii = this.filteredFunctiiByIndex[fromIndex];
        const filteredDirectii = this.filteredDirectiiByIndex[fromIndex];
        const filteredCompartimente = this.filteredCompartimenteByIndex[fromIndex];
        const filteredUsers = this.filteredUsersByIndex[fromIndex];

        // Create a temporary mapping for the move
        const tempControls: any = {};
        const tempFiltered: any = {};

        // Collect all existing controls
        Object.keys(this.functiiControls).forEach(key => {
            const index = parseInt(key);
            tempControls[index] = {
                functie: this.functiiControls[index],
                directie: this.directiiControls[index],
                compartiment: this.compartimenteControls[index],
                user: this.usersControls[index]
            };
            tempFiltered[index] = {
                functii: this.filteredFunctiiByIndex[index],
                directii: this.filteredDirectiiByIndex[index],
                compartimente: this.filteredCompartimenteByIndex[index],
                users: this.filteredUsersByIndex[index]
            };
        });

        // Clear all controls
        this.functiiControls = {};
        this.directiiControls = {};
        this.compartimenteControls = {};
        this.usersControls = {};
        this.filteredFunctiiByIndex = {};
        this.filteredDirectiiByIndex = {};
        this.filteredCompartimenteByIndex = {};
        this.filteredUsersByIndex = {};

        // Rebuild controls with new indices
        const indices = Object.keys(tempControls).map(k => parseInt(k)).sort((a, b) => a - b);

        indices.forEach((oldIndex, arrayIndex) => {
            let newIndex = arrayIndex;

            // Adjust for the move
            if (oldIndex === fromIndex) {
                newIndex = toIndex;
            } else if (fromIndex < toIndex && oldIndex > fromIndex && oldIndex <= toIndex) {
                newIndex = arrayIndex - 1;
            } else if (fromIndex > toIndex && oldIndex >= toIndex && oldIndex < fromIndex) {
                newIndex = arrayIndex + 1;
            }

            this.functiiControls[newIndex] = tempControls[oldIndex].functie;
            this.directiiControls[newIndex] = tempControls[oldIndex].directie;
            this.compartimenteControls[newIndex] = tempControls[oldIndex].compartiment;
            this.usersControls[newIndex] = tempControls[oldIndex].user;

            this.filteredFunctiiByIndex[newIndex] = tempFiltered[oldIndex].functii;
            this.filteredDirectiiByIndex[newIndex] = tempFiltered[oldIndex].directii;
            this.filteredCompartimenteByIndex[newIndex] = tempFiltered[oldIndex].compartimente;
            this.filteredUsersByIndex[newIndex] = tempFiltered[oldIndex].users;
        });
    }

    private reorderAssignments(): void {
        this.assignmentsArray.controls.forEach((control, index) => {
            control.patchValue({ order_priority: index + 1 });
        });
    }

    validateAssignments(): boolean {
        this.validationErrors = [];
        let isValid = true;

        this.assignmentsArray.controls.forEach((control, index) => {
            const value = control.value;

            if (!value.functie_id) {
                this.validationErrors.push({
                    index,
                    field: 'functie_id',
                    message: `Asignarea ${index + 1}: Funcția este obligatorie`
                });
                isValid = false;
            }

            if (!value.directie_id) {
                this.validationErrors.push({
                    index,
                    field: 'directie_id',
                    message: `Asignarea ${index + 1}: Direcția este obligatorie`
                });
                isValid = false;
            }
        });

        // Check for duplicate assignments
        const assignments = this.assignmentsArray.value;
        const duplicates = this.findDuplicateAssignments(assignments);

        duplicates.forEach(duplicate => {
            this.validationErrors.push({
                message: `Asignări duplicate: ${duplicate.indices.join(', ')}`
            });
            isValid = false;
        });

        return isValid;
    }

    private findDuplicateAssignments(assignments: any[]): any[] {
        const duplicates: any[] = [];
        const seen = new Map();

        assignments.forEach((assignment, index) => {
            const key = `${assignment.functie_id}_${assignment.directie_id}_${assignment.compartiment_id || 'null'}_${assignment.user_id || 'null'}`;

            if (seen.has(key)) {
                const existingIndex = seen.get(key);
                duplicates.push({
                    indices: [existingIndex + 1, index + 1],
                    key
                });
            } else {
                seen.set(key, index);
            }
        });

        return duplicates;
    }

    async saveAssignments(): Promise<void> {
        if (!this.validateAssignments()) {
            return;
        }

        this.saving = true;

        try {
            const assignments = this.assignmentsArray.value.map((assignment: any, index: number) => ({
                functie_id: assignment.functie_id,
                directie_id: assignment.directie_id,
                compartiment_id: assignment.compartiment_id === -1 ? null : assignment.compartiment_id,
                user_id: assignment.user_id === -1 ? null : assignment.user_id,
                order_priority: index + 1
            }));

            const response = await this.documentTypesService.updateFunctieAssignments(
                this.data.documentTypeId,
                { assignments }
            ).toPromise();

            if (response?.success) {
                this.showSuccessMessage('Asignările au fost salvate cu succes');
                this.dialogRef.close(true);
            } else {
                this.showErrorMessage('Eroare la salvarea asignărilor');
            }
        } catch (error) {
            console.error('Error saving assignments:', error);
            this.showErrorMessage('Eroare la salvarea asignărilor');
        } finally {
            this.saving = false;
        }
    }

    cancel(): void {
        this.dialogRef.close(false);
    }

    getFunctieDisplayText(functieId: number): string {
        const functie = this.availableData.functii.find(f => f.id === functieId);
        return functie ? `${functie.name} (${functie.acronym || functie.slug})` : '';
    }

    getDirectieDisplayText(directieId: number): string {
        const directie = this.availableData.directii.find(d => d.id_directie === directieId);
        return directie ? directie.name : '';
    }

    getCompartimentDisplayText(compartimentId: number): string {
        const compartiment = this.availableData.compartimente.find(c => c.id_compartiment === compartimentId);
        return compartiment ? compartiment.name : '';
    }

    getUserDisplayText(userId: number): string {
        const user = this.availableData.users.find(u => u.id_user === userId);
        return user ? user.name || user.full_name : '';
    }

    getAssignmentsWithUsers(): number {
        return this.assignmentsArray.controls.filter(control => control.value.user_id).length;
    }

    // Helper methods using the project's notification patterns
    private showSuccessMessage(message: string): void {
        Swal.fire({
            title: 'Succes',
            text: message,
            icon: 'success',
            confirmButtonText: this.helper.dialogConfig.buttonLabels.ok,
        });
    }

    private showErrorMessage(message: string): void {
        this.notificationService.warningSwal(
            this.errorTitle,
            message,
            this.errorIcon
        );
    }
}